import { useEffect, useState } from 'react';
import { api, darf, datum, GEWERK_TEXT, type Anmelder } from '../api.ts';
import {
  Dialog, Fehlerkasten, Haken, Text, ausFehler, type Fehlerlage,
} from './Formular.tsx';
import { Leer } from './Geruest.tsx';
import {
  VORGEFUNDEN_LEER, Vorgefunden, vorgefundenRumpf,
  type VorgefundenEingabe,
} from './Vorgefunden.tsx';
import { TechnikFelder } from './Rueckmeldung.tsx';
import {
  technikAusStand, technikGeaendert, technikRumpf,
  type TechnikEingabe,
} from './technikfelder.ts';

/**
 * „Fertig melden".
 *
 * ORIGINALTEXT MARVIN (20.08.2026)
 *   „er verlangt unten daten, die garnicht zu befuellen sind beim
 *    einblasstop. so koennen wir den auftrag nicht abschliessen und weiter
 *    verarbeiten im system."
 *
 * Der Knopf, an dem früher der ganze Aufmaßteil hing. Er zeigt zuerst, WAS
 * fehlt, und erst dann den Knopf. Ein gesperrter Knopf ohne Grund sieht aus
 * wie ein kaputter Knopf.
 *
 * WAS EIN FREIGESTELLTER ORDNER IST: einer, der sichtbar und befüllbar bleibt
 * und nur das Absenden nicht mehr aufhält. Warum er freigestellt ist, steht
 * in der Zeile. Ohne diesen Satz sucht man morgen wieder nach dem Grund.
 */
export function FertigDialog({
  ich, auftragId, baustelle, zu, fertig,
}: {
  ich: Anmelder; auftragId: string; baustelle?: string;
  zu: () => void; fertig: () => void;
}) {
  const [d, setD] = useState<any>(null);
  const [wahl, setWahl] = useState<Set<string>>(new Set());
  const [restarbeiten, setRestarbeiten] = useState(false);
  /* DIE 3 ANGABEN VON DRAUSSEN STEHEN IN EINEM ZUSTAND UND IN EINEM BAUTEIL.
     Hier standen 3 einzelne Zustände und 3 ausgeschriebene Blöcke - und sie
     standen NUR in diesem Fenster. Marvin, 18.09.2026: „bei dem abmelden der
     beim tiefbau fehlen die anderen checkboxen." Siehe
     `teile/Vorgefunden.tsx`. */
  const [vorgefunden, setVorgefunden] =
    useState<VorgefundenEingabe>(VORGEFUNDEN_LEER);
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const darfUebergehen = darf(ich, 'aufmass.pruefen');

  /* WAS DER TIEFBAU DRAUSSEN VORGEFUNDEN HAT - HIER, WO ER OHNEHIN STEHT.
     ORIGINALTEXT MARVIN (08.09.2026)
       „der Tiefbau hat immer noch nicht die Möglichkeit beim Fertigmelden die
        Rohrlänge, Rohrbreite, Rohrverband und Röhrchen anzugeben."
     Der Weg dafür gibt es seit .138, und er lag hinter dem Block „Technik" in
     der Auftragsakte - also an einer Stelle, an der der Tiefbauer nicht
     vorbeikommt, wenn er die Baustelle verlässt. **Ein Weg, den man nicht
     findet, ist so gut wie keiner.** */
  const [technik, setTechnik] = useState<any>(null);
  const [tech, setTech] = useState<TechnikEingabe>(technikAusStand(null));
  const darfTechnik = darf(ich, 'akte.technik_melden');

  useEffect(() => {
    /* GEFRAGT WIRD NUR MIT DEM RECHT. Ohne es antwortet der Weg mit 403, und
       eine Konsole, in der dauerhaft ein erwarteter Fehler steht, ist eine, in
       der man den unerwarteten übersieht - dieselbe Lehre wie beim
       Firmenzeichen am 27.08.2026. */
    if (!darfTechnik) return;
    api.technikmeldung(auftragId).then((s) => {
      setTechnik(s);
      setTech(technikAusStand(s));
      /* KEIN `setFehler` IM AUFFANGZWEIG. Diese Angaben sind eine Zugabe zum
         Fertigmelden; ein roter Kasten darüber liesse den Tiefbauer glauben,
         die MELDUNG sei gescheitert. Bleibt `technik` leer, steht der Block
         gar nicht erst da. */
    }).catch(() => {});
  }, [auftragId, darfTechnik]);

  /* DIE ORDNERLISTE ZIEHT NACH, SOBALD DER HAKEN STEHT.
     Gerechnet wird die Freistellung auf dem SERVER, wo die Regel steht
     (`regeln/ordner.ts`); sie hier nachzubauen wäre eine zweite Fassung
     derselben 5 Bedingungen. Geschrieben wird dabei nichts - erst beim
     Absenden. Die vorgewählten Gewerke bleiben stehen: sie hängen nicht am
     Haken, und wer gerade abgehakt hat, will seine Auswahl behalten. */
  useEffect(() => {
    let gilt = true;
    api.fertigmeldung(auftragId, { kabelImHaus: vorgefunden.kabel,
                                   keinTiefbau: vorgefunden.keinTiefbau })
      .then((s) => {
      if (!gilt) return;
      setD(s);
      if (vorgefunden.kabel || vorgefunden.keinTiefbau) return;
      // Vorgewählt sind die offenen Gewerke, die UNSERE sind. Ein fremdes
      // Gewerk hakt niemand hier ab.
      setWahl(new Set(s.gewerke
        .filter((g: any) => (!g.erledigt || g.restarbeiten_offen) && s.unsere_gewerke.includes(g.art))
        .map((g: any) => g.art)));
    }).catch((f) => { if (gilt) setFehler(ausFehler(f)); });
    /* DER RENNSCHUTZ. Wer den Haken zweimal schnell umlegt, bekäme sonst die
       Antwort der ersten Frage zuletzt - und die Liste stünde auf dem
       Gegenteil dessen, was der Haken zeigt. */
    return () => { gilt = false; };
  }, [auftragId, vorgefunden.kabel, vorgefunden.keinTiefbau]);

  const zeigeTechnik = !!technik && technik.darf !== false;

  async function melden() {
    setLaeuft(true); setFehler(null);
    try {
      /* ERST DIE RÜCKMELDUNG, DANN DIE FERTIGMELDUNG - und nur, wenn sich
         wirklich etwas geändert hat.
       *
       * DIE REIHENFOLGE IST DER GANZE PUNKT: ginge die Fertigmeldung zuerst
       * durch und die Rückmeldung danach schief, wäre der Auftrag abgegeben
       * UND die berichtigte Pipe verloren - und der Monteur, der übermorgen
       * einbläst, läse den Planwert. Andersherum steht die Korrektur, und der
       * Knopf lässt sich noch einmal drücken.
       *
       * UND SIE HÄLT DIE MELDUNG AUF, statt still zu scheitern: der Fehler
       * steht im Kasten, die Fertigmeldung geht NICHT hinaus. Ein zweiter
       * Anlauf schreibt die Rückmeldung nicht noch einmal - der Stand wird
       * unten nachgezogen, und `technikGeaendert` ist danach falsch. */
      if (zeigeTechnik && technikGeaendert(technik, tech)) {
        await api.technikMelden(auftragId, technikRumpf(tech));
        setTechnik({ ...technik, ...technikRumpf(tech) });
      }
      await api.fertigMelden(auftragId, {
        gewerke: [...wahl],
        restarbeiten_offen: restarbeiten,
        /* NUR WENN GESETZT. Ein fehlendes Feld heisst „dazu sage ich nichts"
           und nicht „nein" - sonst nähme eine Fertigmeldung ohne diesen Haken
           die Angabe des Innendienstes wieder weg. */
        ...vorgefundenRumpf(vorgefunden),
        trotzdem_grund: grund || null,
      });
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  /* ERNEUT EINREICHEN nach einem Freigeben (23.09.2026). Steht hinter
     `melden`, weil es dieselbe Fertigmeldung ist, nur ohne Gewerke. */
  async function neuAbsenden() {
    setLaeuft(true); setFehler(null);
    try {
      await api.fertigMelden(auftragId, { doku_absenden: true, trotzdem_grund: grund || null });
      fertig();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  /* RESTARBEITEN OFFEN ZÄHLT ALS OFFEN (.316), wie am Server. */
  const offen = (d?.gewerke ?? []).filter((g: any) => !g.erledigt || g.restarbeiten_offen);

  return (
    <Dialog titel="Fertig melden" untertitel={baustelle} breite={720}
            laeuft={laeuft} zu={zu}>
      {!d ? <Leer text="Wird geladen." /> : (
        <>
          <Fehlerkasten lage={fehler} />

          {d.bereit ? (
            <div className="hinweis">
              <b>Alle Pflichtordner sind gefüllt.</b>
            </div>
          ) : (
            <div className="hinweis warnung">
              <b>{d.hinweis}</b>
              <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
                {darfUebergehen
                  ? 'Mit einem Grund lässt sich das übergehen. Der Grund steht '
                    + 'danach im Verlauf des Auftrags.'
                  : 'Zum Übergehen fehlt das Recht „Aufmaß prüfen". Die Dateien '
                    + 'lassen sich im Reiter Dateien nachreichen.'}
              </div>
            </div>
          )}

          <h4 className="klein leise" style={{ marginBottom: 6 }}>
            Die Ordner
          </h4>
          <table className="liste">
            <thead>
              <tr>
                <th>Ordner</th>
                <th style={{ width: 90 }} className="zahl">Dateien</th>
                <th style={{ width: 260 }}>Pflicht</th>
              </tr>
            </thead>
            <tbody>
              {d.ordner.map((o: any) => (
                <tr key={o.pfad} style={{ cursor: 'default' }}>
                  <td>
                    {o.pfad}
                    {o.zweck && <div className="klein leise">{o.zweck}</div>}
                  </td>
                  <td className={'zahl' + (o.pflicht && !o.dateien ? ' rot' : '')}>
                    {o.dateien}
                  </td>
                  <td className="klein">
                    {o.pflicht ? (
                      o.dateien
                        ? <span className="marke-zustand z-erledigt">Pflicht, gefüllt</span>
                        : <span className="marke-zustand z-ueberfaellig">Pflicht, leer</span>
                    ) : (
                      <span className="leise">freigestellt: {o.frei}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h4 className="klein leise" style={{ margin: '16px 0 6px' }}>
            Was abgehakt wird
          </h4>
          {/* DAS AUFMASS GEHT MIT (23.09.2026). Marvin: „das problem ist auch
              beim tiefbau." Wer den Tiefbau fertig meldet, sendet sein
              offenes Aufmaß mit ab, wenn es geht - und sieht hier vorher, ob. */}
          {wahl.has('TB') && d.aufmass && d.aufmass.stand === 'offen' && (
            d.aufmass.absendbar ? (
              <div className="hinweis">
                Das Aufmaß ({d.aufmass.positionen} Positionen) wird mit der
                Fertigmeldung abgesendet.
              </div>
            ) : (
              <div className="hinweis warnung">
                <b>Das Aufmaß ist noch nicht abgesendet.</b> {d.aufmass.grund}{' '}
                Der Tiefbau wird trotzdem fertig gemeldet; das Aufmaß bitte im
                Reiter „Aufmaß" erfassen und absenden, sonst geht der Auftrag
                nicht weiter.
              </div>
            )
          )}
          {!offen.length && d.doku_neu_absenden ? (
            /* DIE WIEDER GEÖFFNETE MONTAGE-DOKUMENTATION (23.09.2026). Marvin:
               „er macht alles fertig in seinem bereich aber der auftrag wird
               nicht abgeschlossen." Nach einem Freigeben gab es hier nichts
               mehr zu drücken. */
            <div className="hinweis">
              Alles ist abgehakt, aber die <b>Montage-Dokumentation</b> wurde
              wieder geöffnet. Reiche fehlende Nachweise im Reiter „Dateien"
              nach und schicke sie hier erneut ab.
            </div>
          ) : !offen.length ? (
            <Leer text="Für diesen Auftrag ist schon alles abgehakt." />
          ) : (
            <div className="hakenreihe">
              {offen.map((g: any) => {
                const an = wahl.has(g.art);
                const unser = d.unsere_gewerke.includes(g.art);
                return (
                  <label key={g.art} className={an ? 'an' : undefined}
                         title={unser ? undefined
                           : 'Dieses Gewerk gehört laut Auftragstyp nicht uns.'}>
                    <input type="checkbox" checked={an} onChange={() => {
                      setWahl((w) => {
                        const n = new Set(w);
                        if (n.has(g.art)) n.delete(g.art); else n.add(g.art);
                        return n;
                      });
                    }} />
                    {GEWERK_TEXT[g.art] ?? g.art}
                    {g.termin_datum && ` (${datum(g.termin_datum)})`}
                    {!unser && ', fremd'}
                    {/* RESTARBEITEN (.316): schon gemeldet, aber noch nicht fertig. */}
                    {g.erledigt && g.restarbeiten_offen && ', Restarbeiten abschließen'}
                  </label>
                );
              })}
            </div>
          )}

          {/* WAS SIE DRAUSSEN VORGEFUNDEN HABEN.
              Er steht NUR da, wenn dieser Mensch an diesem Auftrag wirklich
              zurückmelden darf - der Riegel sitzt im Server (`darfMelden`:
              eine Partnerfirma meldet am EIGENEN Tiefbau), hier steht die
              Anzeige dazu. **Ein Feld, das der Server hinterher abweist, ist
              schlimmer als kein Feld.** */}
          {zeigeTechnik && (
            <>
              <h4 className="klein leise" style={{ margin: '16px 0 6px' }}>
                Was du draußen vorgefunden hast
              </h4>
              <div className="hinweis" style={{ marginTop: 0 }}>
                Diese 4 Angaben stammen aus der <b>Planung</b> und weichen
                draußen ab. Was du leer lässt, gilt weiter wie geplant; was
                du einträgst, sehen Montage und Innendienst ab sofort.
              </div>
              <TechnikFelder stand={technik} wert={tech} setze={setTech}
                             fehler={fehler} gesperrt={laeuft} />
            </>
          )}

          {/* „EINGEREICHT" HEISST ABGEGEBEN, NICHT FERTIG.
              Am 12.08.2026 hatte AL Dienstleistungen sein Aufmaß abgegeben UND
              „Restarbeiten offen" gesetzt; beides war richtig, und die
              Automatik lief trotzdem los. Deshalb steht der Haken hier, im
              selben Fenster wie die Meldung. */}
          {/* WAS DRAUSSEN VORGEFUNDEN WURDE - UND ZWAR AN EINER STELLE.
              ==============================================================
              Die 3 Angaben standen hier ausgeschrieben und standen NUR hier.
              Marvin, 18.09.2026: „bei dem abmelden der beim tiefbau fehlen
              die anderen checkboxen ... da ist nur die angabe zur pipe
              drin." Wer sich abmeldet, beantwortet dieselben Fragen, egal
              durch welche Tür er geht - sie stehen jetzt in
              `teile/Vorgefunden.tsx` und erscheinen in diesem Fenster UND in
              „Von der Baustelle zurückmelden". */}
          <Vorgefunden stand={d} wert={vorgefunden} setze={setVorgefunden} />

          <div style={{ marginTop: 12 }}>
            {/* UND ER SCHLIESST KEINE TÜR AUF.
                ORIGINALTEXT MARVIN (03.09.2026, mit Bildschirmfoto)
                  „wenn ich den haken setze bei den «restarbeiten notwendig»
                   kann ich den auftrag aber trotzdem nicht melden, obwohl ja
                   der hinweis ist, dass die Rotberichtigung später erfolgt.
                   aus meiner sicht passt das nicht zusammen."

                **ER HAT RECHT, UND ZWAR AM HINWEIS, NICHT AM RIEGEL.** Der
                Haken sagt etwas über die ZEIT NACH der Abgabe: die
                Rotberichtigung wartet, weil noch etwas zu tun ist. Er sagt
                nichts über die NACHWEISE, die vor der Abgabe im Ordner liegen
                müssen - und genau die fehlten. Der alte Satz las sich, als
                nähme der Haken den Riegel weg; dieser sagt hin, dass er es
                nicht tut. */}
            <Haken name="restarbeiten" titel="Restarbeiten sind noch offen"
                   wert={restarbeiten} setze={setRestarbeiten}
                   hinweis="Abgegeben ist nicht fertig: der Auftrag gilt als
                            eingereicht, und die Rotberichtigung wartet. An den
                            Pflichtordnern ändert der Haken NICHTS - was oben
                            als leer dasteht, hält die Meldung weiter auf." />
          </div>

          {!d.bereit && darfUebergehen && (
            <Text name="trotzdem_grund" titel="Warum trotzdem?" mehrzeilig
                  wert={grund} setze={setGrund} fehler={fehler}
                  hinweis="Ohne diesen Satz geht es nicht weiter. Er steht
                           danach im Verlauf und beantwortet die Frage, warum
                           die Nachweise fehlen." />
          )}

          <div className="reihe" style={{ marginTop: 14 }}>
            {!offen.length && d.doku_neu_absenden && (
              <button className="knopf haupt"
                      disabled={laeuft || (!d.bereit && (!darfUebergehen || !grund.trim()))}
                      onClick={neuAbsenden}>
                {laeuft ? 'Einen Moment' : 'Dokumentation erneut absenden'}
              </button>
            )}
            {!!offen.length && <button className="knopf haupt"
                    disabled={laeuft || !wahl.size
                              || (!d.bereit && (!darfUebergehen || !grund.trim()))}
                    onClick={melden}>
              {laeuft ? 'Einen Moment' : `${wahl.size} fertig melden`}
            </button>}
            <button className="knopf" disabled={laeuft} onClick={zu}>
              Abbrechen
            </button>
            {/* EIN GESPERRTER KNOPF NENNT SEINEN GRUND - UND ZWAR HIER, NEBEN
                DEM KNOPF. Er stand bisher oben im Kasten, drei Bildschirmhöhen
                entfernt: wer unten auf einen grauen Knopf sieht, rollt nicht
                nach oben, er hält ihn für kaputt. */}
            {!wahl.size && !!offen.length && (
              <span className="klein leise">
                Kein Gewerk gewählt.
              </span>
            )}
            {!d.bereit && !darfUebergehen && (
              <span className="klein leise">
                Es fehlen Pflichtordner ({(d.fehlend ?? []).join(', ')}), und
                zum Übergehen fehlt das Recht „Aufmaß prüfen".
              </span>
            )}
            {!d.bereit && darfUebergehen && !grund.trim() && (
              <span className="klein leise">
                Es fehlen Pflichtordner ({(d.fehlend ?? []).join(', ')}).
                Ohne einen Grund für das Übergehen geht es nicht weiter.
              </span>
            )}
          </div>
        </>
      )}
    </Dialog>
  );
}
